import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { ArrowClockwise, Receipt, Warning, ToggleLeft, ToggleRight, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ALL_SCRAPERS = [
  // API-backed sources
  { key: 'adzuna_enabled',    label: 'Adzuna',       type: 'api'     },
  { key: 'jooble_enabled',    label: 'Jooble',       type: 'api'     },
  // Engine-level scrapers
  { key: 'ddgs_enabled',      label: 'DuckDuckGo',   type: 'engine'  },
  { key: 'searxng_enabled',   label: 'SearXNG',      type: 'engine'  },
  { key: 'jobspy_enabled',    label: 'JobSpy',        type: 'engine'  },
  { key: 'crawl4ai_enabled',  label: 'Crawl4AI',     type: 'engine'  },
  { key: 'jina_reader_enabled', label: 'Jina Reader', type: 'engine' },
  // Site scrapers
  { key: 'linkedin_enabled',  label: 'LinkedIn',     type: 'scraper' },
  { key: 'indeed_enabled',    label: 'Indeed',       type: 'scraper' },
  { key: 'glassdoor_enabled', label: 'Glassdoor',    type: 'scraper' },
  { key: 'naukri_enabled',    label: 'Naukri',       type: 'scraper' },
  { key: 'instahyre_enabled', label: 'Instahyre',    type: 'scraper' },
  { key: 'cutshort_enabled',  label: 'Cutshort',     type: 'scraper' },
  { key: 'hirist_enabled',    label: 'Hirist',       type: 'scraper' },
  { key: 'iimjobs_enabled',   label: 'IIMJobs',      type: 'scraper' },
  { key: 'arbeitnow_enabled', label: 'Arbeitnow',    type: 'scraper' },
  { key: 'himalayas_enabled', label: 'Himalayas',    type: 'scraper' },
  { key: 'remotive_enabled',  label: 'Remotive',     type: 'scraper' },
  { key: 'jobicy_enabled',    label: 'Jobicy',        type: 'scraper' },
  { key: 'ycwas_enabled',     label: 'YC Work at a Startup', type: 'scraper' },
];

function UsageBar({ hits, limit, threshold }) {
  if (!limit) return null;
  const pct = Math.min((hits / limit) * 100, 100);
  const isAlert = hits >= threshold;
  return (
    <div className="mt-1">
      <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isAlert ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-zinc-600 mt-0.5">
        <span>{hits} / {limit} hits</span>
        {isAlert && <span className="text-red-400 flex items-center gap-1"><Warning size={10} /> Alert threshold reached</span>}
      </div>
    </div>
  );
}

export default function AdminCostTab() {
  const { fetchApiUsage, fetchScraperToggles, updateScraperToggles, invalidateSearchCache } = useApp();
  const [usage, setUsage] = useState(null);
  const [toggles, setToggles] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flushing, setFlushing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([fetchApiUsage(), fetchScraperToggles()]);
      setUsage(u);
      setToggles(t);
      setDraft(t);
    } catch {
      toast.error('Could not load cost data');
    } finally {
      setLoading(false);
    }
  }, [fetchApiUsage, fetchScraperToggles]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (key) => {
    setDraft(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAlertField = (key, value) => {
    const parsed = key.includes('email') ? value : parseInt(value, 10);
    if (!key.includes('email') && isNaN(parsed)) return;
    setDraft(prev => ({ ...prev, [key]: parsed }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateScraperToggles(draft);
      setToggles(draft);
      toast.success('Scraper toggles saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleFlushSearchCache = async () => {
    if (!window.confirm("Flush all cached search results? Next searches will rerun scrapers (slower, but reflects new toggles immediately).")) {
      return;
    }
    setFlushing(true);
    try {
      const res = await invalidateSearchCache("search:*");
      toast.success(`Flushed ${res.keys_flushed ?? 0} search cache key${res.keys_flushed === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cache flush failed');
    } finally {
      setFlushing(false);
    }
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(toggles);

  const alertCfg = usage?.alert_config || {};
  const usageBySource = Object.fromEntries((usage?.sources || []).map(s => [s.source, s]));

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt size={24} className="text-amber-500" />
          <h2 className="text-sm font-bold text-white">Cost & Sources</h2>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-bold bg-amber-500 text-black rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
          <button
            onClick={handleFlushSearchCache}
            disabled={flushing}
            title="Flush Redis search-result cache (search:*)"
            className="px-3 py-1.5 text-xs font-semibold border border-white/10 hover:border-amber-500/40 hover:text-amber-400 text-zinc-400 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            <Trash size={12} />
            {flushing ? 'Flushing…' : 'Flush Search Cache'}
          </button>
          <button onClick={load} className={`p-2 hover:bg-white/5 rounded-full transition-colors ${loading ? 'animate-spin' : ''}`}>
            <ArrowClockwise size={16} />
          </button>
        </div>
      </div>

      {/* API Usage Cards */}
      <div className="grid grid-cols-2 gap-4">
        {['adzuna', 'jooble'].map(src => {
          const row = usageBySource[src] || { hits: 0 };
          const limit = alertCfg[`${src}_limit`] || 0;
          const threshold = alertCfg[`${src}_alert_threshold`] || 0;
          return (
            <div key={src} className="jp-card p-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-white uppercase">{src}</span>
                <span className="text-[10px] text-zinc-500">API Source</span>
              </div>
              <div className="text-3xl font-mono text-amber-400">{row.hits ?? 0}</div>
              <div className="text-[10px] text-zinc-500 mb-2">hits this month</div>
              <UsageBar hits={row.hits ?? 0} limit={limit} threshold={threshold} />
              {row.last_hit && (
                <div className="text-[10px] text-zinc-600 mt-2">
                  Last hit: {new Date(row.last_hit).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Alert Config */}
      <div className="jp-card p-5">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
          <Warning size={12} /> Alert Thresholds
        </h3>
        {draft && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'adzuna_limit',           label: 'Adzuna Limit',      type: 'number' },
              { key: 'adzuna_alert_threshold',  label: 'Adzuna Alert At',   type: 'number' },
              { key: 'jooble_limit',            label: 'Jooble Limit',      type: 'number' },
              { key: 'jooble_alert_threshold',  label: 'Jooble Alert At',   type: 'number' },
              { key: 'admin_alert_email',       label: 'Alert Email',       type: 'email', span: 2 },
            ].map(({ key, label, type, span }) => (
              <div key={key} className={span ? `col-span-${span}` : ''}>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</label>
                <input
                  type={type === 'number' ? 'number' : 'email'}
                  value={draft[key] ?? ''}
                  onChange={e => handleAlertField(key, e.target.value)}
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scraper Toggles */}
      <div className="jp-card overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Source Toggles</h3>
        </div>
        <div className="p-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
          {draft && ALL_SCRAPERS.map(({ key, label, type }) => {
            const enabled = draft[key] ?? true;
            return (
              <button
                key={key}
                onClick={() => handleToggle(key)}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                  enabled
                    ? 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400'
                    : 'border-zinc-700 bg-white/[0.02] text-zinc-500'
                }`}
              >
                <div className="text-left">
                  <div className="text-xs font-semibold">{label}</div>
                  <div className="text-[10px] opacity-60 uppercase">{type}</div>
                </div>
                {enabled
                  ? <ToggleRight size={20} weight="fill" />
                  : <ToggleLeft size={20} />}
              </button>
            );
          })}
        </div>
        {isDirty && (
          <div className="px-5 py-3 border-t border-white/5 text-xs text-amber-400">
            Unsaved changes — click "Save Changes" above to apply.
          </div>
        )}
      </div>
    </div>
  );
}
